import * as React from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/material';
import { withResourceProtection } from 'app/components/ReusableUIComponents/HOCs/ResourceProtected/withResourceProtection';
import {
  HeaderDropdownMAIQ,
  HeaderDropdownMenuItemMAIQ,
} from 'app/components/ReusableUIComponents/LowLevelComponents/HeaderDropdownMAIQ';
import { useAgentTypeDropdown } from './useAgentTypeDropdown';
import {
  AGENT_TYPE_DROPDOWN_RESOURCE,
  AGENT_TYPE_NONE_LABEL,
  AGENT_TYPE_NONE_VALUE,
  AGENT_TYPE_OPTIONS,
} from './constants';

type AgentTypeDropdownProps = {
  className?: string;
  disabled?: boolean;
  /**
   * `header` (default) — profile-like header dropdown via HeaderDropdownMAIQ.
   * `sidebar` — theme-aware MUI Select, full-width, suitable for AI Avatar sidebar.
   */
  variant?: 'header' | 'sidebar';
};

const getDisplayLabel = (selected: string): string => {
  if (!selected || selected === AGENT_TYPE_NONE_VALUE) {
    return AGENT_TYPE_NONE_LABEL;
  }
  const match = AGENT_TYPE_OPTIONS.find(opt => opt.value === selected);
  return match ? match.label : AGENT_TYPE_NONE_LABEL;
};

const AgentTypeDropdownComponent: React.FC<AgentTypeDropdownProps> = ({
  className,
  disabled,
  variant = 'header',
}) => {
  const { value, handleChange, selectAgentType } = useAgentTypeDropdown();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (optionValue: string) => {
    selectAgentType(optionValue);
    handleClose();
  };

  if (variant === 'sidebar') {
    const sidebarSx = {
      color: 'text.primary',
      width: '100%',
      height: '40px',
      marginTop: 1,
      fontSize: '0.875rem',
      fontWeight: 500,
      '& .MuiSelect-icon': { color: 'text.secondary' },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'divider',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'text.secondary',
      },
    } as const;

    return (
      <Select
        className={className}
        labelId="agent-type-select-label"
        id="agent-type-select"
        displayEmpty
        disabled={disabled}
        value={value}
        onChange={handleChange}
        sx={sidebarSx}
        renderValue={selected => getDisplayLabel(selected)}
      >
        <MenuItem value={AGENT_TYPE_NONE_VALUE}>{AGENT_TYPE_NONE_LABEL}</MenuItem>
        {AGENT_TYPE_OPTIONS.map(option => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <HeaderDropdownMAIQ
      id="header-agent-type-select"
      className={className}
      label={getDisplayLabel(value)}
      open={open}
      anchorEl={anchorEl}
      onOpen={handleOpen}
      onClose={handleClose}
      disabled={disabled}
      minWidth={220}
      menuMinWidth={220}
      aria-label="Select agent type"
    >
      <HeaderDropdownMenuItemMAIQ
        selected={value === AGENT_TYPE_NONE_VALUE}
        onClick={() => handleSelect(AGENT_TYPE_NONE_VALUE)}
      >
        {AGENT_TYPE_NONE_LABEL}
      </HeaderDropdownMenuItemMAIQ>
      {AGENT_TYPE_OPTIONS.map(option => (
        <HeaderDropdownMenuItemMAIQ
          key={option.value}
          selected={value === option.value}
          disabled={option.disabled}
          onClick={() => handleSelect(option.value)}
        >
          {option.label}
        </HeaderDropdownMenuItemMAIQ>
      ))}
    </HeaderDropdownMAIQ>
  );
};

export const AgentTypeDropdown = withResourceProtection(
  AgentTypeDropdownComponent,
  AGENT_TYPE_DROPDOWN_RESOURCE,
  null,
);

export default AgentTypeDropdown;
